import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { calculateYTG, calculateYTGPercentage, copySummaryTableToClipboard, getOrderedMonthNames, isForecastInPast, isPeriodInPast } from '../Utilities/tableUtils';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import Tooltip from '@mui/material/Tooltip';
import './SkuBaseForecastTable.css';

const OutputsSummaryTable = ({ summaryData, view, startMonth, selectedForecast, aggregationPeriod = "monthly", showActuals = false, loadingActuals = false, showCompare = false, groupBy: groupByProp = "channel", comparisonData = null, loadingComparison = false, isComparingForecasts = false, onToggleFullscreen, isFullscreen = false }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [expandedCustomers, setExpandedCustomers] = useState(new Set());
    const [stickyColumnsEnabled, setStickyColumnsEnabled] = useState(true);
    const groupBy = groupByProp === 'channel' ? 'list' : groupByProp;

    const currentYear = new Date().getFullYear();
    const currentCalendarMonth = new Date().getMonth() + 1; // 1-12
    const selectedYear = selectedForecast?.startDate ? new Date(selectedForecast.startDate).getFullYear() : currentYear;
    const totalPeriods = aggregationPeriod === "monthly" ? 12 : (summaryData?.totals?.[view]?.length - 1 || 12);
    
    const showYTG = !isForecastInPast(selectedYear, currentYear, summaryData?.currentMonth, totalPeriods, startMonth, currentCalendarMonth);

    // Use period names from API response if available, otherwise fall back to month names
    const rawPeriodNames = summaryData?.periodNames || getOrderedMonthNames(startMonth);
    // Build period names array: always filter out YTG/FY, then add them back
    // YTG is conditionally included based on showYTG to match header rendering
    let basePeriodNames = [...(rawPeriodNames.filter(name => name !== 'YTG' && name !== 'FY'))];
    if (showYTG) {
        basePeriodNames.push('YTG');
    }
    basePeriodNames.push('FY');
    
    // Format period names with week numbers if it's weekly aggregation
    const periodNames = aggregationPeriod === "weekly" 
        ? basePeriodNames.map((period, index) => {
            // Check if this looks like a date (DD-MM-YYYY or DD-MM-YY format) 
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period; // Keep FY, etc. as is
        })
        : basePeriodNames;

    const currencyPrefix = ['grossRev', 'netRev', 'fixedSpend', 'grossProfit'].includes(view) ? '£' : '';
    const percentPostfix = view === 'grossProfitPercent' ? '%' : '';

    const formatValue = (value) => {
        if (value === undefined || value === null) return 'N/A';
        const roundedValue = Math.round(value * 100) / 100;
        const displayValue = roundedValue % 1 === 0 ? roundedValue.toString() : roundedValue.toFixed(2);
        return `${currencyPrefix}${displayValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${percentPostfix}`;
    };

    const formatVariance = (value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        const formatted = formatValue(value);
        return value > 0 ? `+${formatted}` : formatted;
    };

    const actualKey = view === 'units' ? 'actualUnits' 
        : view === 'grossRev' ? 'actualGrossRev' 
        : view === 'triggers' ? 'actualTriggers'
        : view === 'cogs' ? 'actualCogs'
        : view === 'logs' ? 'actualLogs'
        : null;

    const getActualValue = (entity, index) => {
        if (!actualKey || !entity) {
            return null;
        }
        const series = entity[actualKey];
        if (!Array.isArray(series)) {
            return null;
        }
        const value = series[index];
        return value === undefined ? null : value;
    };

    const getComparisonValue = (entityType, entityIdentifier, index) => {
        if (!isComparingForecasts || !comparisonData) {
            return null;
        }

        let comparisonEntity = null;

        if (entityType === 'totals') {
            comparisonEntity = comparisonData.totals;
        } else if (entityType === 'sku') {
            comparisonEntity = comparisonData.skus?.find(s => s.skuNumber === entityIdentifier);
        } else if (entityType === 'customer') {
            comparisonEntity = comparisonData.customers?.find(c => c.name === entityIdentifier);
        } else if (entityType === 'channel') {
            comparisonEntity = comparisonData.channels?.find(ch => ch.name === entityIdentifier);
        } else if (entityType === 'skuCustomer') {
            const [skuNumber, customerName] = entityIdentifier.split('|');
            const sku = comparisonData.skus?.find(s => s.skuNumber === skuNumber);
            comparisonEntity = sku?.customers?.find(c => c.name === customerName);
        } else if (entityType === 'customerSku') {
            const [customerName, skuNumber] = entityIdentifier.split('|');
            const customer = comparisonData.customers?.find(c => c.name === customerName);
            comparisonEntity = customer?.skus?.find(s => s.skuNumber === skuNumber);
        } else if (entityType === 'channelCustomer') {
            const [channelName, customerName] = entityIdentifier.split('|');
            const channel = comparisonData.channels?.find(ch => ch.name === channelName);
            comparisonEntity = channel?.customers?.find(c => c.name === customerName);
        } else if (entityType === 'channelCustomerSku') {
            const [channelName, customerName, skuNumber] = entityIdentifier.split('|');
            const channel = comparisonData.channels?.find(ch => ch.name === channelName);
            const customer = channel?.customers?.find(c => c.name === customerName);
            comparisonEntity = customer?.skus?.find(s => s.skuNumber === skuNumber);
        } else if (entityType === 'item') {
            const { customerName, skuNumber, description } = entityIdentifier || {};
            comparisonEntity = comparisonData.items?.find(item =>
                item.customerName === customerName &&
                item.skuNumber === skuNumber &&
                item.description === description);
        }

        if (!comparisonEntity) {
            return null;
        }

        const series = comparisonEntity[view];
        if (!Array.isArray(series)) {
            return null;
        }

        const mainPeriodName = basePeriodNames[index];
        const comparisonPeriodNames = comparisonData.periodNames;
        
        if (!mainPeriodName || !comparisonPeriodNames) {
            const value = series[index];
            return value === undefined ? null : value;
        }

        const comparisonIndex = comparisonPeriodNames.findIndex(name => name === mainPeriodName);
        
        if (comparisonIndex === -1)
            return null;

        const value = series[comparisonIndex];
        return value === undefined ? null : value;
    };

    const renderComparisonLayout = (topValue, mainValue, variance, isLoading, varianceInverted = false) => {
        const varianceDisplay = isLoading
            ? '…'
            : variance !== null
                ? formatVariance(variance)
                : '—';
        
        const varianceColor = variance === null || isLoading
            ? 'text.secondary'
            : variance > 0
                ? 'success.main'
                : variance < 0
                    ? 'error.main'
                    : 'text.secondary';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                <Typography
                    variant="caption"
                    component="span"
                    color="text.secondary"
                    sx={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                >
                    {topValue}
                </Typography>
                <Typography 
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 'bold' }}
                >
                    {mainValue}
                </Typography>
                <Typography
                    variant="caption"
                    component="span"
                    sx={{ display: 'block', whiteSpace: 'nowrap', color: varianceColor }}
                >
                    {varianceDisplay}
                </Typography>
            </Box>
        );
    };

    const renderValueCell = (forecastValue, actualValue, comparisonValue = null, periodIndex = null) => {
        const forecastIsNumber = typeof forecastValue === 'number' && !Number.isNaN(forecastValue);

        if (isComparingForecasts && comparisonValue !== undefined) {
            const hasComparison = comparisonValue !== null && comparisonValue !== undefined;
            const variance = hasComparison && forecastIsNumber ? forecastValue - comparisonValue : null;
            
            const comparisonDisplay = loadingComparison
                ? '…'
                : hasComparison
                    ? formatValue(comparisonValue)
                    : '—';

            return renderComparisonLayout(
                comparisonDisplay,
                formatValue(forecastValue),
                variance,
                loadingComparison
            );
        }

        if (!showActuals || !actualKey) {
            return formatValue(forecastValue);
        }

        const hasActual = actualValue !== null && actualValue !== undefined;
        const variance = hasActual && forecastIsNumber ? actualValue - forecastValue : null;
        
        const isPast = periodIndex !== null && isPeriodInPast(
            periodIndex,
            summaryData?.periodNames || rawPeriodNames,
            startMonth,
            selectedYear,
            aggregationPeriod
        );

        if (!showCompare) {
            const displayValue = hasActual ? actualValue : (isPast ? null : forecastValue);
            return (
                <Typography 
                    variant="body2"
                    sx={{ 
                        color: hasActual ? 'primary.main' : 'inherit',
                        fontWeight: hasActual ? 'bold' : 'normal'
                    }}
                >
                    {loadingActuals ? '…' : (displayValue !== null ? formatValue(displayValue) : '—')}
                </Typography>
            );
        }

        const actualDisplay = loadingActuals
            ? '…'
            : hasActual
                ? formatValue(actualValue)
                : (isPast ? '—' : '—');

        return renderComparisonLayout(
            formatValue(forecastValue),
            actualDisplay,
            variance,
            loadingActuals
        );
    };

    const handleRowExpand = (rowId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
                if (groupBy === 'customer') {
                    setExpandedCustomers(prevCustomers => {
                        const newCustomerSet = new Set(prevCustomers);
                        summaryData.channels?.forEach(channel => {
                            if (channel.name === rowId) {
                                channel.customers?.forEach(customer => {
                                    newCustomerSet.delete(customer.name);
                                });
                            }
                        });
                        return newCustomerSet;
                    });
                }
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    };

    const handleCustomerExpand = (channelName, customerName) => {
        setExpandedCustomers(prev => {
            const newSet = new Set(prev);
            const key = `${channelName}|${customerName}`;
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const copyToClipboard = async () => {
        await copySummaryTableToClipboard(summaryData, view, startMonth, selectedYear, currentYear, aggregationPeriod, showActuals, groupBy, expandedRows, expandedCustomers);
    };

    const expandAll = () => {
        if (groupBy === 'sku') {
            const allSkuNumbers = new Set(summaryData.skus?.map(sku => sku.skuNumber) || []);
            setExpandedRows(allSkuNumbers);
        } else if (groupBy === 'customer') {
            const allChannelNames = new Set(summaryData.channels?.map(channel => channel.name) || []);
            setExpandedRows(allChannelNames);
            
            const allCustomerKeys = new Set();
            summaryData.channels?.forEach(channel => {
                channel.customers?.forEach(customer => {
                    allCustomerKeys.add(`${channel.name}|${customer.name}`);
                });
            });
            setExpandedCustomers(allCustomerKeys);
        }
    };

    const collapseAll = () => {
        setExpandedRows(new Set());
        setExpandedCustomers(new Set());
    };

    const hasCollapsedRows = () => {
        if (groupBy === 'sku') {
            const totalSkus = summaryData.skus?.length || 0;
            return totalSkus > expandedRows.size;
        } else if (groupBy === 'customer') {
            const totalChannels = summaryData.channels?.length || 0;
            const totalCustomers = summaryData.channels?.reduce((sum, channel) => 
                sum + (channel.customers?.length || 0), 0) || 0;
            return totalChannels > expandedRows.size || totalCustomers > expandedCustomers.size;
        }
        return false;
    };

    const hasExpandedRows = () => {
        return expandedRows.size > 0 || expandedCustomers.size > 0;
    };

    const dataRowHoverStyle = {
        '&:hover > *': {
            backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)'
        },
        '&:hover > th, &:hover > .sticky-cell': {
            backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 1) !important'
                : 'rgba(245, 245, 245, 1) !important'
        }
    };

    // ✅ Early return if summaryData is missing or malformed
    if (!summaryData || !summaryData.totals) {
        return <div>No data available</div>;
    }

    // Calculate number of periods (excluding FY total)
    const periodCount = (summaryData.totals?.[view] || []).length - 1;
    const fyIndex = periodCount;

    const stickyColumnCount = groupBy === 'list' ? 4 : 1;

    const getColumnWidth = (columnIndex) => {
        if (groupBy === 'list') {
            if (columnIndex === 0) return 50;
            if (columnIndex === 1) return 200;
            if (columnIndex === 2) return 120;
            if (columnIndex === 3) return 250;
        }
        return 'auto';
    };

    const getColumnLeft = (columnIndex) => {
        if (groupBy === 'list') {
            if (columnIndex === 0) return 0;
            if (columnIndex === 1) return 50;
            if (columnIndex === 2) return 250;
            if (columnIndex === 3) return 370;
        }
        return 0;
    };

    const getStickyColumnStyle = (columnIndex, customBgColor) => {
        const style = {
            width: getColumnWidth(columnIndex),
            minWidth: getColumnWidth(columnIndex)
        };
        
        if (stickyColumnsEnabled) {
            style.position = 'sticky';
            style.left = getColumnLeft(columnIndex);
            style.zIndex = 2;
            style.boxShadow = columnIndex === stickyColumnCount - 1 ? '2px 0 4px rgba(0,0,0,0.1)' : 'none';
        }
        
        if (customBgColor) {
            style.backgroundColor = customBgColor;
        } else {
            style.backgroundColor = (theme) => theme.palette.background.paper;
        }
        
        return style;
    };

    return (
        <TableContainer 
            component={Paper} 
            sx={{ 
                height: '100%',
                overflow: 'auto',
                maxWidth: '100%',
                display: 'inline-block',
                width: 'fit-content',
                minWidth: '100%'
            }}
        >
            <Table aria-label="account summary table" stickyHeader sx={{ 
                tableLayout: 'auto', 
                width: 'auto',
                '& .MuiTableBody-root .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                },
                '& .MuiTableHead-root .MuiTableCell-root[data-week-header="true"]': {
                    whiteSpace: 'pre'
                }
            }}>
                <TableHead>
                    <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                        <TableCell sx={{ ...getStickyColumnStyle(0), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {onToggleFullscreen && (
                                    <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                                        <IconButton
                                            onClick={onToggleFullscreen}
                                            size="small"
                                            sx={{ p: 0.5 }}
                                        >
                                            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title={stickyColumnsEnabled ? "Unpin columns" : "Pin columns"}>
                                    <IconButton
                                        onClick={() => setStickyColumnsEnabled(prev => !prev)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    >
                                        {stickyColumnsEnabled ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Copy table to clipboard">
                                    <IconButton
                                        onClick={copyToClipboard}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {(groupBy === 'sku' || groupBy === 'customer') && hasCollapsedRows() && (
                                    <Tooltip title="Expand all">
                                        <IconButton
                                            onClick={expandAll}
                                            size="small"
                                            sx={{ p: 0.5 }}
                                        >
                                            <UnfoldMoreIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {(groupBy === 'sku' || groupBy === 'customer') && hasExpandedRows() && (
                                    <Tooltip title="Collapse all">
                                        <IconButton
                                            onClick={collapseAll}
                                            size="small"
                                            sx={{ p: 0.5 }}
                                        >
                                            <UnfoldLessIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </TableCell>
                        {groupBy === 'list' && (
                            <>
                                <TableCell sx={{ ...getStickyColumnStyle(1), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>Customer</TableCell>
                                <TableCell sx={{ ...getStickyColumnStyle(2), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>SKU</TableCell>
                                <TableCell sx={{ ...getStickyColumnStyle(3), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>Description</TableCell>
                            </>
                        )}
                        {periodNames.map((period, index) => {
                            const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                            const isYtgOrFy = period === 'YTG' || period === 'FY';
                            return (
                                <TableCell 
                                    key={`${period}-${index}`} 
                                    align="right" 
                                    sx={{ 
                                        fontWeight: isYtgOrFy ? 'bold' : 'normal',
                                        whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                        backgroundColor: (theme) => theme.palette.background.paper
                                    }}
                                    data-week-header={isWeekHeader}
                                >
                                    {period.toUpperCase()}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* ✅ TOTALS Row (from API) */}
                    {groupBy === 'list' ? (
                        <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                            <TableCell sx={getStickyColumnStyle(0)}></TableCell>
                            <TableCell component="th" scope="row" sx={getStickyColumnStyle(1)}>TOTAL</TableCell>
                            <TableCell sx={getStickyColumnStyle(2)}></TableCell>
                            <TableCell sx={getStickyColumnStyle(3)}></TableCell>
                            {(summaryData.totals?.[view] || []).slice(0, periodCount).map((total, index) => (
                                <TableCell align="right" key={index}>
                                    {renderValueCell(
                                        total, 
                                        getActualValue(summaryData.totals, index),
                                        getComparisonValue('totals', null, index),
                                        index
                                    )}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        view === 'grossProfitPercent'
                                            ? calculateYTGPercentage(summaryData.totals?.grossProfit, summaryData.totals?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                            : calculateYTG(summaryData.totals?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                        null,
                                        null,
                                        null
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {renderValueCell(
                                    (summaryData.totals?.[view] || [])[fyIndex],
                                    getActualValue(summaryData.totals, fyIndex),
                                    getComparisonValue('totals', null, fyIndex),
                                    fyIndex
                                )}
                            </TableCell>
                        </TableRow>
                    ) : (
                        <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                            <TableCell component="th" scope="row" sx={getStickyColumnStyle(0)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(groupBy === 'sku' || groupBy === 'customer') && <Box sx={{ width: 24 }} />}
                                    TOTAL
                                </Box>
                            </TableCell>
                            {(summaryData.totals?.[view] || []).slice(0, periodCount).map((total, index) => (
                                <TableCell align="right" key={index}>
                                    {renderValueCell(
                                        total, 
                                        getActualValue(summaryData.totals, index),
                                        getComparisonValue('totals', null, index),
                                        index
                                    )}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        view === 'grossProfitPercent'
                                            ? calculateYTGPercentage(summaryData.totals?.grossProfit, summaryData.totals?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                            : calculateYTG(summaryData.totals?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                        null,
                                        null,
                                        null
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {renderValueCell(
                                    (summaryData.totals?.[view] || [])[fyIndex],
                                    getActualValue(summaryData.totals, fyIndex),
                                    getComparisonValue('totals', null, fyIndex),
                                    fyIndex
                                )}
                            </TableCell>
                        </TableRow>
                    )}

                    {/* SKU groupBy mode - show SKUs with expandable customers */}
                    {groupBy === 'sku' && summaryData.skus?.map((sku) => (
                        <React.Fragment key={sku.skuNumber}>
                            {/* SKU Row with expansion */}
                            <TableRow 
                                sx={{ 
                                    cursor: 'pointer',
                                    '& > *': { 
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.05)' 
                                            : 'rgba(0, 0, 0, 0.04)'
                                    },
                                    '&:hover > *': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.08)'
                                            : 'rgba(0, 0, 0, 0.08)'
                                    },
                                    '&:hover > th': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(30, 30, 30, 1) !important' 
                                            : 'rgba(238, 238, 238, 1) !important'
                                    }
                                }}
                                onClick={() => handleRowExpand(sku.skuNumber)}
                            >
                                <TableCell component="th" scope="row" sx={{ 
                                    ...getStickyColumnStyle(0),
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(18, 18, 18, 1)' 
                                        : 'rgba(245, 245, 245, 1)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRowExpand(sku.skuNumber);
                                            }}
                                            sx={{ p: 0 }}
                                        >
                                            {expandedRows.has(sku.skuNumber) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                        {sku.skuNumber} - {sku.description}
                                    </Box>
                                </TableCell>
                                {(sku?.[view] || []).slice(0, periodCount).map((value, index) => (
                                    <TableCell align="right" key={index}>
                                        {renderValueCell(
                                            value, 
                                            getActualValue(sku, index),
                                            getComparisonValue('sku', sku.skuNumber, index),
                                            index
                                        )}
                                    </TableCell>
                                ))}
                                {showYTG && (
                                    <TableCell align="right">
                                        {renderValueCell(
                                            view === 'grossProfitPercent'
                                                ? calculateYTGPercentage(sku?.grossProfit, sku?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                                : calculateYTG(sku?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                            null,
                                            null,
                                            null
                                        )}
                                    </TableCell>
                                )}
                                <TableCell align="right">
                                    {renderValueCell(
                                        (sku?.[view] || [])[fyIndex],
                                        getActualValue(sku, fyIndex),
                                        getComparisonValue('sku', sku.skuNumber, fyIndex),
                                        fyIndex
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Customer Rows (shown when SKU is expanded) */}
                            {expandedRows.has(sku.skuNumber) && sku.customers?.map((customer) => (
                                <TableRow key={`${sku.skuNumber}-${customer.name}`} sx={dataRowHoverStyle}>
                                    <TableCell component="th" scope="row" sx={{ ...getStickyColumnStyle(0), pl: 6 }}>{customer.name}</TableCell>
                                    {(customer?.[view] || []).slice(0, periodCount).map((value, index) => (
                                        <TableCell align="right" key={index}>
                                            {renderValueCell(
                                                value, 
                                                getActualValue(customer, index),
                                                getComparisonValue('skuCustomer', `${sku.skuNumber}|${customer.name}`, index),
                                                index
                                            )}
                                        </TableCell>
                                    ))}
                                    {showYTG && (
                                        <TableCell align="right">
                                            {renderValueCell(
                                                view === 'grossProfitPercent'
                                                    ? calculateYTGPercentage(customer?.grossProfit, customer?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                                    : calculateYTG(customer?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                                null,
                                                null,
                                                null
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell align="right">
                                        {renderValueCell(
                                            (customer?.[view] || [])[fyIndex],
                                            getActualValue(customer, fyIndex),
                                            getComparisonValue('skuCustomer', `${sku.skuNumber}|${customer.name}`, fyIndex),
                                            fyIndex
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}

                    {/* Customer groupBy mode - show channels with expandable customers with expandable SKUs */}
                    {groupBy === 'customer' && summaryData.channels?.map((channel) => (
                        <React.Fragment key={channel.name}>
                            {/* Channel Row with expansion */}
                            <TableRow 
                                sx={{ 
                                    cursor: 'pointer',
                                    '& > *': { 
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.05)' 
                                            : 'rgba(0, 0, 0, 0.04)'
                                    },
                                    '&:hover > *': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.08)'
                                            : 'rgba(0, 0, 0, 0.08)'
                                    },
                                    '&:hover > th': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(30, 30, 30, 1) !important' 
                                            : 'rgba(238, 238, 238, 1) !important'
                                    }
                                }}
                                onClick={() => handleRowExpand(channel.name)}
                            >
                                <TableCell component="th" scope="row" sx={{ 
                                    ...getStickyColumnStyle(0),
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(18, 18, 18, 1)' 
                                        : 'rgba(245, 245, 245, 1)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRowExpand(channel.name);
                                            }}
                                            sx={{ p: 0 }}
                                        >
                                            {expandedRows.has(channel.name) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                        {channel.name}
                                    </Box>
                                </TableCell>
                                {(channel?.[view] || []).slice(0, periodCount).map((value, index) => (
                                    <TableCell align="right" key={index}>
                                        {renderValueCell(
                                            value, 
                                            getActualValue(channel, index),
                                            getComparisonValue('channel', channel.name, index),
                                            index
                                        )}
                                    </TableCell>
                                ))}
                                {showYTG && (
                                    <TableCell align="right">
                                        {renderValueCell(
                                            view === 'grossProfitPercent'
                                                ? calculateYTGPercentage(channel?.grossProfit, channel?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                                : calculateYTG(channel?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                            null,
                                            null,
                                            null
                                        )}
                                    </TableCell>
                                )}
                                <TableCell align="right">
                                    {renderValueCell(
                                        (channel?.[view] || [])[fyIndex],
                                        getActualValue(channel, fyIndex),
                                        getComparisonValue('channel', channel.name, fyIndex),
                                        fyIndex
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Customer Rows (shown when channel is expanded) */}
                            {expandedRows.has(channel.name) && channel.customers?.map((customer) => (
                                <React.Fragment key={`${channel.name}-${customer.name}`}>
                                    <TableRow 
                                        sx={{ 
                                            cursor: 'pointer',
                                            '& > *': { 
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.02)' 
                                                    : 'rgba(0, 0, 0, 0.02)'
                                            },
                                            '&:hover > *': {
                                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.06)'
                                                    : 'rgba(0, 0, 0, 0.06)'
                                            },
                                            '&:hover > th': {
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                                    ? 'rgba(25, 25, 25, 1) !important' 
                                                    : 'rgba(245, 245, 245, 1) !important'
                                            }
                                        }}
                                        onClick={() => handleCustomerExpand(channel.name, customer.name)}
                                    >
                                        <TableCell component="th" scope="row" sx={{ 
                                            ...getStickyColumnStyle(0),
                                            backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                                ? 'rgba(18, 18, 18, 1)' 
                                                : 'rgba(250, 250, 250, 1)',
                                            pl: 6 
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    aria-label="expand customer"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCustomerExpand(channel.name, customer.name);
                                                    }}
                                                    sx={{ p: 0 }}
                                                >
                                                    {expandedCustomers.has(`${channel.name}|${customer.name}`) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                </IconButton>
                                                {customer.name}
                                            </Box>
                                        </TableCell>
                                        {(customer?.[view] || []).slice(0, periodCount).map((value, index) => (
                                            <TableCell align="right" key={index}>
                                                {renderValueCell(
                                                    value, 
                                                    getActualValue(customer, index),
                                                    getComparisonValue('channelCustomer', `${channel.name}|${customer.name}`, index),
                                                    index
                                                )}
                                            </TableCell>
                                        ))}
                                        {showYTG && (
                                            <TableCell align="right">
                                                {renderValueCell(
                                                    view === 'grossProfitPercent'
                                                        ? calculateYTGPercentage(customer?.grossProfit, customer?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                                        : calculateYTG(customer?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                                    null,
                                                    null,
                                                    null
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            {renderValueCell(
                                                (customer?.[view] || [])[fyIndex],
                                                getActualValue(customer, fyIndex),
                                                getComparisonValue('channelCustomer', `${channel.name}|${customer.name}`, fyIndex),
                                                fyIndex
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {/* SKU Rows (shown when customer is expanded) */}
                                    {expandedCustomers.has(`${channel.name}|${customer.name}`) && customer.skus?.map((sku) => (
                                        <TableRow key={`${channel.name}-${customer.name}-${sku.skuNumber}`} sx={dataRowHoverStyle}>
                                            <TableCell component="th" scope="row" sx={{ ...getStickyColumnStyle(0), pl: 10 }}>
                                                {sku.skuNumber} - {sku.description}
                                            </TableCell>
                                            {(sku?.[view] || []).slice(0, periodCount).map((value, index) => (
                                                <TableCell align="right" key={index}>
                                                    {renderValueCell(
                                                        value, 
                                                        getActualValue(sku, index),
                                                        getComparisonValue('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, index),
                                                        index
                                                    )}
                                                </TableCell>
                                            ))}
                                            {showYTG && (
                                                <TableCell align="right">
                                                    {renderValueCell(
                                                        view === 'grossProfitPercent'
                                                            ? calculateYTGPercentage(sku?.grossProfit, sku?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                                            : calculateYTG(sku?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                                        null,
                                                        null,
                                                        null
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell align="right">
                                                {renderValueCell(
                                                    (sku?.[view] || [])[fyIndex],
                                                    getActualValue(sku, fyIndex),
                                                    getComparisonValue('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, fyIndex),
                                                    fyIndex
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}

                    {/* List mode (formerly channel) - flat table with Customer/SKU/Description columns */}
                    {groupBy === 'list' && summaryData.items?.map((item, itemIndex) => (
                        <TableRow key={`list-${itemIndex}`} sx={dataRowHoverStyle}>
                            <TableCell className="sticky-cell" sx={getStickyColumnStyle(0)} />
                            <TableCell component="th" scope="row" className="sticky-cell" sx={getStickyColumnStyle(1)}>{item.customerName}</TableCell>
                            <TableCell className="sticky-cell" sx={getStickyColumnStyle(2)}>{item.skuNumber}</TableCell>
                            <TableCell className="sticky-cell" sx={getStickyColumnStyle(3)}>{item.description}</TableCell>
                            {(item?.[view] || []).slice(0, periodCount).map((value, index) => (
                                <TableCell align="right" key={index}>
                                    {renderValueCell(
                                        value, 
                                        getActualValue(item, index),
                                        getComparisonValue('item', item, index),
                                        index
                                    )}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        view === 'grossProfitPercent'
                                            ? calculateYTGPercentage(item?.grossProfit, item?.netRev, summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth)
                                            : calculateYTG(item?.[view], summaryData.currentMonth, selectedYear, currentYear, aggregationPeriod, startMonth, currentCalendarMonth),
                                        null,
                                        null,
                                        null
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {renderValueCell(
                                    (item?.[view] || [])[fyIndex],
                                    getActualValue(item, fyIndex),
                                    getComparisonValue('item', item, fyIndex),
                                    fyIndex
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OutputsSummaryTable;
